import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InputBase from "@mui/material/InputBase";

export const ZoneContainer = styled(Box)(({ mr }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  marginRight: mr ? 16 : 0,
  marginBottom: 0,
}));

export const DropLabel = styled(Typography)({
  fontWeight: 600,
  color: "#555",
  fontSize: 11,
  marginBottom: 6,
  marginLeft: 0,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

export const DropZone = styled(Box, {
  shouldForwardProp: (prop) => prop !== "dragover",
})(({ dragover }) => ({
  width: "100%",
  minHeight: 100,
  margin: 0,
  border: dragover ? "2px dotted rgb(44, 52, 123)" : "1.5px dotted rgb(44, 52, 123)",
  borderRadius: 8,
  background: dragover 
    ? "linear-gradient(135deg, #eef3ff 0%, #e6edff 100%)" 
    : "linear-gradient(135deg, #f8fafe 0%, #f3f7fe 100%)",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  padding: "10px",
  boxSizing: "border-box",
  transition: "all 0.3s ease",
  boxShadow: dragover 
    ? "0 4px 16px rgba(44, 52, 123, 0.15), inset 0 1px 3px rgba(44, 52, 123, 0.08)" 
    : "inset 0 1px 2px rgba(44, 52, 123, 0.04)",
  overflowY: "visible",
  overflowX: "hidden",
  "&:hover": {
    borderColor: "rgba(44, 52, 123, 0.8)",
    borderWidth: "2px",
    background: dragover 
      ? "linear-gradient(135deg, #eef3ff 0%, #e6edff 100%)" 
      : "linear-gradient(135deg, #f0f5ff 0%, #e8eeff 100%)",
    boxShadow: "0 2px 8px rgba(44, 52, 123, 0.12), inset 0 1px 3px rgba(44, 52, 123, 0.06)",
  },
}));

export const DropText = styled(Box)({
  color: "#aaa",
  fontSize: 12,
  textAlign: "center",
  margin: "0 auto",
  alignSelf: "center",
  justifySelf: "center",
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 400,
});

export const DropItemCard = styled(Box)({
  display: "flex",
  alignItems: "center",
  background: "#fff",
  borderRadius: 5,
  border: "1px solid #e0e0e0",
  padding: "3px 8px 3px 6px",
  marginBottom: 4,
  height: 24,
  minHeight: 24,
  maxHeight: 24,
  boxShadow: "0 1px 2px rgba(25, 118, 210, 0.05)",
  fontSize: 11,
  lineHeight: 1.2,
  transition: "box-shadow 0.15s, border-color 0.15s, transform 0.15s, background 0.15s",
  borderLeft: "3px solid #1976d2",
  cursor: "grab",
  userSelect: "none",
  whiteSpace: "nowrap",
  overflow: "hidden",
  position: "relative",
  '&:hover': {
    borderColor: "#1976d2",
    boxShadow: "0 2px 8px rgba(25, 118, 210, 0.12)",
    background: "#f5faff",
    transform: "translateY(-1px)",
  },
  '&:active': {
    transform: "scale(0.98)",
    boxShadow: "0 1px 2px rgba(25, 118, 210, 0.08)",
  },
});

export const DropIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isVisible",
})(({ isVisible }) => ({
  height: 2,
  backgroundColor: "rgb(44, 52, 123)",
  borderRadius: 1,
  margin: "2px 0",
  opacity: isVisible ? 1 : 0,
  transition: "opacity 0.2s ease",
  pointerEvents: "none",
  boxShadow: "0 0 4px rgba(44, 52, 123, 0.5)",
}));

export const DropTypeBadge = styled("span")({
  background: "#f3f6fa",
  color: "#1976d2",
  borderRadius: 3,
  fontSize: 9,
  fontWeight: 600,
  padding: "0 4px",
  marginLeft: 6,
  marginRight: 2,
  letterSpacing: 0.2,
  textTransform: "uppercase",
  display: "inline-block",
  lineHeight: 1,
});

export const ShimmerRow = styled(Box)({
  height: 28,
  margin: '4px 0',
  borderRadius: 4,
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%)',
  backgroundSize: '400% 100%',
  animation: 'shimmer 1.2s ease-in-out infinite',
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '-400px 0' },
    '100%': { backgroundPosition: '400px 0' },
  },
});

export const DropdownOption = styled(Box)(({ theme, isSelected }) => ({
  padding: "8px",
  minHeight: "28px",
  fontSize: "12px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  borderRadius: "3px",
  color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
  backgroundColor: isSelected
    ? theme.palette.mode === "dark"
      ? theme.palette.primary.main + '1F'
      : theme.palette.primary.main + '10'
    : "transparent",
  transition: theme.transitions.create(["background-color", "transform"], {
    duration: theme.transitions.duration.shortest,
  }),
  margin: "1px 2px",
  "&:hover": {
    backgroundColor: isSelected
      ? theme.palette.mode === "dark"
        ? theme.palette.primary.main + '2E'
        : theme.palette.primary.main + '15'
      : theme.palette.action.hover,
  },
}));

export const SelectionIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})(({ theme, isSelected }) => ({
  width: 14,
  height: 14,
  flexShrink: 0,
  borderRadius: "2px",
  border: `1.5px solid ${
    isSelected ? theme.palette.primary.main : theme.palette.grey[400]
  }`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginRight: 6,
  backgroundColor: isSelected ? theme.palette.primary.main : "transparent",
  transition: theme.transitions.create(["border-color", "background-color"], {
    duration: theme.transitions.duration.shortest,
  }),
  "& .MuiSvgIcon-root": {
    fontSize: 10,
    color: "#fff",
    opacity: isSelected ? 1 : 0,
    transform: isSelected ? "scale(1)" : "scale(0.5)",
    transition: theme.transitions.create(["opacity", "transform"], {
      duration: theme.transitions.duration.shortest,
    }),
  },
}));

/** Radio-style indicator (circle) for single-select filter options. Selected = ring + inner dot, not fully filled. */
export const RadioIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})(({ theme, isSelected }) => ({
  width: 14,
  height: 14,
  flexShrink: 0,
  borderRadius: "50%",
  border: `1.5px solid ${
    isSelected ? theme.palette.primary.main : theme.palette.grey[400]
  }`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginRight: 6,
  backgroundColor: "transparent",
  transition: theme.transitions.create(["border-color"], {
    duration: theme.transitions.duration.shortest,
  }),
  ...(isSelected && {
    "&::after": {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: "50%",
      backgroundColor: theme.palette.primary.main,
    },
  }),
}));

export const DropdownSearchInput = styled(InputBase)(({ theme }) => ({
  width: "100%",
  padding: "2px 8px",
  height: 28,
  fontSize: 12,
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: 2,
  position: "sticky",
  top: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 2,
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  "& input": {
    padding: 0,
    fontSize: 12,
    "&::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 0.7,
      fontSize: 11,
    },
  },
}));

// Tab-related styled components
export const CompactTabsContainer = styled(Box)({
  width: '100%',
  padding: '4px 8px 2px 8px',
  borderBottom: '1px solid #e0e0e0',
  backgroundColor: '#fafafa',
});

export const CompactTabButton = styled(Box)(({ theme, isActive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 8px',
  marginRight: '2px',
  borderRadius: '4px 4px 0 0',
  backgroundColor: isActive ? '#fff' : 'transparent',
  border: isActive ? '1px solid #e0e0e0' : '1px solid transparent',
  borderBottom: isActive ? '1px solid #fff' : '1px solid transparent',
  color: isActive ? 'rgb(44, 52, 123)' : '#666',
  fontSize: '11px',
  fontWeight: isActive ? 600 : 400,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  minHeight: '24px',
  maxWidth: '120px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: isActive ? '#fff' : '#f0f0f0',
    color: isActive ? 'rgb(44, 52, 123)' : '#333',
  },
}));

export const CompactAddTabButton = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  marginLeft: '4px',
  borderRadius: '3px',
  backgroundColor: 'transparent',
  border: '1px solid rgb(44, 52, 123)',
  color: 'rgb(44, 52, 123)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f0f4ff',
    borderColor: 'rgba(44, 52, 123, 0.8)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '14px',
  },
}));
